import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconSizeEnum,
  RecIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Clock } from '@/components/Clock';
import { CurrentUsers } from '@/components/CurrentUsers/CurrentUsers';
import { HelpTour } from '@/components/HelpTour/HelpTour';
import { RenderEnrichementsIcons } from '@/components/RenderEnrichementsIcons/RenderEnrichementsIcons';
import { Shorcut } from '@/components/Shorcut/Shorcut';
import { StartTimeEndTimePart } from '@/components/StartTimeEndTimePart/StartTimeEndTimePart';
import { StatusSeenChipSelect } from '@/components/StatusSeenChipSelect';
import { TProgramBroadcast } from '@/features/broadcast-schedule/types';
import { useChannelId } from '@/features/broadcasters/api/get-channel-id';
import { ProgramPriorityEnum } from '@/features/programs/types';
import { TReviewStateValues } from '@/features/segments/types';
import { useProgramPresence } from '@/providers/websocket';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { useReport } from '@/stores/report-store';

import { usePatchProgramBroadcast } from '../../api/patch-broadcast-schedule';
import { useParrillaSegmentsTour } from '../../hooks/use-parrilla-segments-tour';

type TBroadcastScheduleProgramHeaderNameProps = {
  item?: TProgramBroadcast;
};
export function BroadcastScheduleProgramHeaderName({
  item,
}: TBroadcastScheduleProgramHeaderNameProps) {
  const { t } = useTranslation();
  const userConnectedInPrograms = useProgramPresence();
  const { data: channelData } = useChannelId({ id: item?.channel_id || '' });
  const { setFilters } = useProgramBroadcastFilters();
  const { setReport, setReportedId, setReportType } = useReport();
  const { startTour } = useParrillaSegmentsTour();

  const { mutate: updateBroadcastProgram } = usePatchProgramBroadcast();

  const handleUpdateProgram = (reviewState: string) => {
    if (!item) return;

    if (reviewState === '5') {
      setReportType('program');
      setReport(true);
      setReportedId(item.id);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { program_id, ...rest } = item || {};
    const data: Omit<TProgramBroadcast, 'program_id'> = {
      ...rest,
      review_state: parseInt(reviewState) as TReviewStateValues,
      priority: item?.priority as ProgramPriorityEnum,
      channel_id: item?.channel_id || '',
      start_datetime: item?.start_datetime || '',
      end_datetime: item?.end_datetime || '',
      language_id: item?.language_id || '',
      audience: item?.audience ?? null,
      audience_program_source_id: item?.audience_program_source_id ?? null,
      economical_value: item?.economical_value ?? null,
      name: item?.name ?? '',
      id: item?.id ?? '',
    };

    updateBroadcastProgram(data);
  };

  const isInStreaming = useMemo(() => {
    if (!item?.start_datetime || !item?.end_datetime) return false;

    const isInStreaming =
      new Date() >= new Date(item?.start_datetime) && new Date() <= new Date(item?.end_datetime);

    return isInStreaming;
  }, [item?.start_datetime, item?.end_datetime]);

  useEffect(() => {
    setFilters({
      isStreaming: isInStreaming,
    });
  }, [isInStreaming, setFilters]);

  if (!item) return null;

  return (
    <div className="flex flex-wrap items-center">
      <Typography
        fontSize={FontSizeEnum.Xl}
        weight={FontWeightEnum.Bold}
        color={FontColorEnum.Dark10}
      >
        {t('Programa')}:
      </Typography>
      <Typography
        fontSize={FontSizeEnum.Xl}
        weight={FontWeightEnum.Regular}
        color={FontColorEnum.Secondary}
      >
        &nbsp;{item?.name}
      </Typography>
      <div className="flex flex-1 items-center justify-end gap-2">
        <div className="flex items-center gap-2" id="parrilla-segments-info">
          <div className="flex items-center gap-1">
            {channelData?.name && (
              <>
                <Typography
                  fontSize={FontSizeEnum.Xs}
                  color={FontColorEnum.Medium}
                  weight={FontWeightEnum.Semibold}
                >
                  {channelData?.name}
                </Typography>
                <Typography
                  fontSize={FontSizeEnum.Xs}
                  color={FontColorEnum.Medium}
                  weight={FontWeightEnum.Semibold}
                >
                  -
                </Typography>
              </>
            )}

            <StartTimeEndTimePart
              startDateTime={item?.start_datetime}
              endDateTime={item?.end_datetime}
            />
          </div>
          <RenderEnrichementsIcons
            enrichments={{
              ocr: item.parameters?.enrichments?.ocr,
              stt: item.parameters?.enrichments?.stt,
              logos: item.parameters?.enrichments?.logos,
              peoples: item.parameters?.enrichments?.peoples,
              advertising: item.parameters?.enrichments?.advertising,
            }}
          />
          {typeof item.review_state === 'number' && (
            <StatusSeenChipSelect
              value={item.review_state}
              onChange={handleUpdateProgram}
              isProgram
            />
          )}
        </div>

        {isInStreaming && <RecIcon className="animate-fade-in-out !text-feedback-error-20" />}
        {userConnectedInPrograms[item?.id || ''] && (
          <CurrentUsers ids={userConnectedInPrograms[item?.id || '']} limit={3} />
        )}
        <div className="flex items-center gap-2">
          <Clock fontSize={FontSizeEnum.Md} iconSize={IconSizeEnum.SM} />
          <Shorcut />
          <HelpTour tour={startTour} />
        </div>
      </div>
    </div>
  );
}
